import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ArtistSearchAdminItem } from '@bailafm/shared';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ApiClientError, artistsApi, featuredArtistsApi } from '@/lib/api';
import { cn } from '@/lib/utils';

// Gestor de artistas destacados de la home. Una sola lista ordenada
// (singleton): buscar artistas del Core, añadirlos, reordenar con
// drag&drop, eliminar, y guardar con If-Match. Mismo patrón que
// CollectionFormDialog (buscador + dnd) + DestacadosPage (If-Match).

const SEARCH_DEBOUNCE_MS = 300;

export default function ArtistasDestacadosPage() {
  const [selected, setSelected] = useState<ArtistSearchAdminItem[]>([]);
  const [etag, setEtag] = useState<string | undefined>(undefined);
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<ArtistSearchAdminItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Carga inicial: el singleton (ids ordenados) + todos los artistas
  // del Core para hidratar nombre/avatar de los ya destacados.
  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [item, all] = await Promise.all([
        featuredArtistsApi.get(),
        artistsApi.search(''),
      ]);
      const byId = new Map(all.map((a) => [a.id, a]));
      const ordered = [...item.artists]
        .sort((a, b) => a.position - b.position)
        .map(
          (ref) =>
            byId.get(ref.artistId) ?? {
              id: ref.artistId,
              name: `(artista ${ref.artistId} no disponible)`,
            },
        );
      setSelected(ordered);
      setSavedIds(item.artists.map((a) => a.artistId));
      setEtag(item._etag);
    } catch (err) {
      setLoadError(
        err instanceof ApiClientError
          ? err.message
          : 'No se pudieron cargar los artistas destacados.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Búsqueda con debounce + descarte de respuestas tardías.
  const requestSeqRef = useRef(0);
  useEffect(() => {
    const term = search.trim();
    if (term.length === 0) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    const seq = ++requestSeqRef.current;
    setSearching(true);
    setSearchError(null);
    const handle = window.setTimeout(() => {
      artistsApi
        .search(term)
        .then((items) => {
          if (seq !== requestSeqRef.current) return;
          setResults(items);
        })
        .catch((err) => {
          if (seq !== requestSeqRef.current) return;
          setResults([]);
          setSearchError(
            err instanceof ApiClientError ? err.message : 'No se pudo buscar.',
          );
        })
        .finally(() => {
          if (seq !== requestSeqRef.current) return;
          setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search]);

  // Cierra el dropdown al pulsar fuera.
  useEffect(() => {
    if (!searchOpen) return;
    function handle(event: PointerEvent) {
      const root = searchContainerRef.current;
      if (root && !root.contains(event.target as Node)) setSearchOpen(false);
    }
    document.addEventListener('pointerdown', handle);
    return () => document.removeEventListener('pointerdown', handle);
  }, [searchOpen]);

  const selectedIds = useMemo(
    () => new Set(selected.map((a) => a.id)),
    [selected],
  );

  const dirty = useMemo(() => {
    const current = selected.map((a) => a.id);
    if (current.length !== savedIds.length) return true;
    return current.some((id, i) => id !== savedIds[i]);
  }, [selected, savedIds]);

  const handleAdd = useCallback((artist: ArtistSearchAdminItem) => {
    setSelected((prev) =>
      prev.some((a) => a.id === artist.id) ? prev : [...prev, artist],
    );
    setSearch('');
    setResults([]);
    setSearchOpen(false);
  }, []);

  const handleRemove = useCallback((id: string) => {
    setSelected((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSelected((prev) => {
      const oldIndex = prev.findIndex((a) => a.id === active.id);
      const newIndex = prev.findIndex((a) => a.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await featuredArtistsApi.update(etag, {
        artistIds: selected.map((a) => a.id),
      });
      setSavedIds(updated.artists.map((a) => a.artistId));
      setEtag(updated._etag);
      toast.success('Artistas destacados guardados');
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? formatApiError(err)
          : 'No se pudieron guardar los artistas destacados.';
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">
            Artistas destacados
          </h1>
          <p className="text-muted-foreground">
            Elige y ordena los artistas que aparecen en la home. El orden se
            modifica arrastrando.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!dirty || saving || loading}>
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </header>

      {loadError ? (
        <div className="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">{loadError}</p>
          <Button variant="outline" size="sm" onClick={() => void refresh()}>
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Buscar artistas
            </h3>
            <div ref={searchContainerRef} className="relative max-w-xl">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => {
                  if (search.trim().length > 0) setSearchOpen(true);
                }}
                placeholder="Empieza a escribir para buscar…"
                className="pl-9"
                autoComplete="off"
              />
              {searching && (
                <Loader2
                  className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground"
                  aria-hidden
                />
              )}
              {searchOpen && (results.length > 0 || searching || searchError) && (
                <div
                  role="listbox"
                  className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
                >
                  {searchError ? (
                    <p className="px-2 py-1.5 text-sm text-destructive">
                      {searchError}
                    </p>
                  ) : results.length === 0 ? (
                    <p className="px-2 py-1.5 text-sm text-muted-foreground">
                      Buscando…
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {results.map((artist) => {
                        const already = selectedIds.has(artist.id);
                        return (
                          <li key={artist.id}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={already}
                              onClick={() => handleAdd(artist)}
                              disabled={already}
                              className={cn(
                                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                                already
                                  ? 'cursor-default opacity-50'
                                  : 'hover:bg-accent hover:text-accent-foreground',
                              )}
                            >
                              <ArtistThumb url={artist.avatarUrl} />
                              <span className="min-w-0 flex-1 truncate font-medium">
                                {artist.name}
                              </span>
                              {already ? (
                                <span className="text-xs text-muted-foreground">
                                  añadido
                                </span>
                              ) : (
                                <Plus
                                  className="size-4 text-muted-foreground"
                                  aria-hidden
                                />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="space-y-3">
            <header className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Artistas en la home
              </h3>
              <span className="text-xs text-muted-foreground">
                {selected.length}{' '}
                {selected.length === 1 ? 'artista' : 'artistas'}
              </span>
            </header>
            {loading ? (
              <p className="rounded-md border bg-card p-4 text-sm text-muted-foreground">
                Cargando…
              </p>
            ) : selected.length === 0 ? (
              <p className="rounded-md border border-dashed bg-card p-4 text-center text-sm text-muted-foreground">
                Aún no has añadido ningún artista.
              </p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selected.map((a) => a.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="max-w-xl space-y-2">
                    {selected.map((artist) => (
                      <SortableArtistRow
                        key={artist.id}
                        artist={artist}
                        onRemove={() => handleRemove(artist.id)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </section>

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}
        </>
      )}
    </div>
  );
}

interface SortableArtistRowProps {
  artist: ArtistSearchAdminItem;
  onRemove(): void;
}

function SortableArtistRow({ artist, onRemove }: SortableArtistRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: artist.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'z-10')}>
      <article
        className={cn(
          'flex items-center gap-3 rounded-lg border bg-card p-2',
          isDragging && 'shadow-lg ring-2 ring-foreground/20',
        )}
      >
        <button
          type="button"
          aria-label="Arrastra para reordenar"
          className="flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
        <ArtistThumb url={artist.avatarUrl} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {artist.name}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Quitar artista"
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive"
        >
          <X className="size-4" aria-hidden />
        </Button>
      </article>
    </li>
  );
}

function ArtistThumb({ url }: { url: string | undefined }) {
  if (!url) {
    return <div aria-hidden className="size-10 shrink-0 rounded-full bg-muted" />;
  }
  return (
    <img
      src={url}
      alt=""
      className="size-10 shrink-0 rounded-full object-cover"
      loading="lazy"
    />
  );
}

function formatApiError(err: ApiClientError): string {
  if (err.code === 'etag_mismatch') {
    return 'Otra persona modificó la lista. Recarga la página y vuelve a intentarlo.';
  }
  return err.message || 'No se pudieron guardar los artistas destacados.';
}
